import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from '../../context/Auth.context';
import { ProfileContext } from '../../context/Profile.context';
import { withRouter } from '../../components/WithRouter';
import { CircleCheck, CircleX, CircleDotDashed, FileCheck, FileClock, UserSearch } from 'lucide-react';
import { Box, Card, CardContent, List, ListItem, ListItemText, Locale } from '@otcmarketsgroup/otcm-ui-lib';
import { appStatuses } from '../../constants';
import { testIds } from '../../constants/testIds';
import CancelForm from '../CancelForm';
import SaveForm from '../SaveForm';
import StartForm from '../StartForm';
import ReopenForm from '../ReopenForm/ReopenForm';
import VerificationHistory from '../VerificationHistory/VerificationHistory';
import ProfileStatusDialog from './ProfileStatusDialog';

const getStatusDialogSessionKey = symbol => `profile-control-panel-status-dialog:${symbol}`;

const STATUS_CONFIG = {
  IN_PROGRESS: { icon: <CircleDotDashed color='var(--otc-orange)' size={20} /> },
  SUBMITTED: { icon: <FileCheck color='var(--otc-green)' size={20} /> },
  UNDER_REVIEW: { icon: <UserSearch color='var(--otc-green)' size={20} /> },
  PENDING_REVIEW: { icon: <UserSearch color='var(--otc-green)' size={20} /> },
};

const ProfileControlPanel = ({ params: { symbol } }) => {
  const [authState] = useContext(AuthContext);
  const [profileState] = useContext(ProfileContext);
  const [showStatusDialog, setShowStatusDialog] = useState(() => {
    if (typeof window === 'undefined') return true;
    const dialogToken = window.sessionStorage.getItem(getStatusDialogSessionKey(symbol));
    return !dialogToken;
  });
  const [startTrigger, setStartTrigger] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const { profile, verifiedProfile, verifiedDate } = profileState;
  const { status: formStatus } = profile;
  const user = authState?.user;
  const userEmail = user?.email;
  const isNoApplication = formStatus?.id === appStatuses.NO_APPLICATION;
  const isInProgress = formStatus?.id === appStatuses.IN_PROGRESS;
  const isSubmitted = formStatus?.id === appStatuses.SUBMITTED;
  const statusConfig = STATUS_CONFIG[formStatus?.id];

  const dismissStatusDialog = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(getStatusDialogSessionKey(symbol), 'dismissed');
    }
    setShowStatusDialog(false);
  };

  const handleStartUpdate = () => {
    dismissStatusDialog();
    setStartTrigger(prev => prev + 1);
  };

  const handleOpenCancel = () => {
    dismissStatusDialog();
    setCancelDialogOpen(true);
  };

  const handleOpenReopen = () => {
    dismissStatusDialog();
    setReopenDialogOpen(true);
  };

  return (
    <>
      <ProfileStatusDialog
        open={showStatusDialog}
        statusId={formStatus?.id}
        onDismiss={dismissStatusDialog}
        onStartUpdate={handleStartUpdate}
        onOpenCancel={handleOpenCancel}
        onOpenReopen={handleOpenReopen}
      />

      <Card>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2} data-testid={testIds.controlPanel.root}>
            <List>
              <ListItem
                startIcon={verifiedProfile
                  ? <CircleCheck color='var(--otc-green)' size={20} />
                  : <CircleX color='var(--otc-warning)' size={20} />}
                alignIconToTop>
                <ListItemText
                  primary='Verified Profile'
                  secondary={verifiedProfile ? <Locale type='date'>{verifiedDate}</Locale> : null}
                />
              </ListItem>
              {statusConfig && (
                <ListItem startIcon={statusConfig.icon}>
                  <ListItemText primary={formStatus.description} />
                </ListItem>
              )}
              {!isNoApplication && <ListItem startIcon={<FileClock color='var(--otc-green)' size={20} />} alignIconToTop>
                <ListItemText primary='Last Modified' secondary={<>
                  {profile.lastUpdatedBy}
                  <br />
                  {profile.lastUpdated ? <Locale type='date'>{profile.lastUpdated}</Locale> : null}
                </>} />
              </ListItem>}
              <VerificationHistory symbol={symbol} />
            </List>
            {isNoApplication && <StartForm symbol={symbol} email={userEmail} triggerStart={startTrigger} />}
            {(isInProgress || isSubmitted) && <Box display='flex' flexDirection='column' gap={1}>
              {isInProgress && <SaveForm email={userEmail} />}
              {isSubmitted && <ReopenForm symbol={symbol} email={userEmail} open={reopenDialogOpen} onOpenChange={setReopenDialogOpen} />}
              <CancelForm symbol={symbol} email={userEmail} open={cancelDialogOpen} onOpenChange={setCancelDialogOpen} />
            </Box>}
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

ProfileControlPanel.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string,
    formSection: PropTypes.string
  })
};

export default withRouter(ProfileControlPanel);
